<script setup>
/**
 * Imports
 */
import { HeaderUserComponentSettings } from "@/components/layout/header/components";
import { useAuthStore } from "@/modules/auth/store";
import { useMenu, usePerson } from "@/composables";
import { confirmLogout } from "@/shared/utils";
import { useRouter } from "vue-router";
import { computed } from "vue";

/**
 * Variables
 */
const authStore = useAuthStore();
const { user } = authStore;
const { shortName } = usePerson(user);
const router = useRouter();

// меню профиля
const { isMenuOpen, closeMenu } = useMenu();
// подменю действий в хедере
const { isMenuOpen: activeDropdown, closeMenu: closeDropdown, toggleMenu: toggleDropdown } = useMenu();

/**
 * Computed
 */
const roles = computed(() => user.roles?.map((r) => r.name_ru).join(", "));

/**
 * Methods
 */
const logout = () => {
  confirmLogout(async () => {
    authStore.logout();
    await router.push({ name: "login" });
  });
};
</script>

<template>
  <div class="user">
    <div
      v-click-outside="closeDropdown"
      @click="toggleDropdown"
      class="user__actions"
      :class="{ 'user__actions--active': activeDropdown }"
    >
      <div class="user__img">
        <img src="/assets/images/avatar.png" alt="аватар" />
      </div>
      <div class="user__info">
        <span class="user__name">{{ shortName }}</span>
        <span class="user__role" v-if="roles">{{ roles }}</span>
      </div>
      <IconComponent class="user__down-icon" name="chevron-down" />
      <div class="user__drop">
        <!--        <div class="user__drop-item" @click="showMenu">-->
        <!--          <span>Профиль</span>-->
        <!--        </div>-->
        <button class="user__drop-item" @click.prevent="logout">
          <IconComponent name="log-out" />
          <span>Выйти</span>
        </button>
      </div>
    </div>
  </div>
  <div :class="['review-modal__overlay', { active: isMenuOpen }]" @click.self="closeMenu">
    <transition name="slide-right-modal">
      <div v-if="isMenuOpen" class="review-modal">
        <button class="profile-modal__close" @click="closeMenu">×</button>
        <div class="review-modal-title__wrapper">
          <h2>Профиль</h2>
        </div>
        <HeaderUserComponentSettings v-model:user="user" />
      </div>
    </transition>
  </div>
</template>

<style lang="stylus">
.review-modal-title__wrapper {
  padding: 2rem 1.5rem 1.5rem 1.5rem;
  padding-bottom: 3rem;
  border-bottom: 1px solid #E5E5E5;
}
.user {
  display flex
  gap 10px
  align-items center
  height 100%
  margin-left: auto

  &__actions {
    position relative
    display flex
    gap 20px
    align-items center
		padding: 10px
		border-radius: 12px
		cursor: pointer
		transition background var(--transition)

		&:hover,
		&--active {
			background var(--gray-2)
		}

    &--active {
      .user__drop {
        visibility visible
        opacity 1
        z-index 10
      }
    }
  }

  &__drop {
    visibility hidden
    opacity 0
    position absolute
    width 172px
    top: calc(100% + 10px)
    right 0
    background var(--white)
    padding 12px
    box-shadow: 0 4px 30px rgba(46, 45, 116, 0.1);
		border-radius 12px
		transition: var(--transition)

    &-item {
      display flex
      align-items center
      justify-content flex-end
			gap: 10px
      padding: 10px
			background: none
			border: none
			border-radius 12px
			color: var(--gray);
			font-size: 0.875rem
			font-style: normal;
			font-weight: 500;
			line-height: normal;
      width: 100%
      transition var(--transition)

			&:hover {
				background var(--gray-2)
			}

      span {
        font-weight 500
        font-size 0.875rem
        line-height 20px
      }

      .icon {
        width 24px
        height 24px
      }
    }
  }

  &__img {
    width 40px
    height 40px
    object-fit cover
    object-position center
    border-radius 100%
    background var(--gray-dark)
    overflow hidden
    color transparent
    display inline-flex
    align-items center
    justify-content center
    flex-shrink 0
  }

  &__info {
    display flex
    flex-direction column
		gap: 4px
  }

  &__name,
  &__role {
		color: var(--black-5)
		font-size: 0.875rem
		font-style: normal;
		font-weight: 500;
		line-height: normal;
  }

  &__role {
    color: var(--gray)
    font-size: 0.75rem
  }

  &__down-icon {
    color: var(--gray)
  }
}

.review-modal__overlay {
  position: fixed;
  z-index: 1000;
  left: 0; right: 0; top: 0; bottom: 0;
  background-color: rgba(143, 143, 143, 0.3);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.25s ease-in-out;
}
.review-modal {
  width 30%
  min-width 600px
  height: 100vh;
  background: white;
  box-shadow: 0 4px 32px rgba(0,0,0,0.18);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  transform: translateX(100%);
  transition transform 0.4s ease-in-out
  position absolute
  top 0
  right 0
}

.review-modal__overlay.active {
  opacity: 1;
  pointer-events: auto;
  & .review-modal {
    transform: translateX(0);
  }
}
.slide-right-modal-enter-active,
.slide-right-modal-leave-active {
  transition: transform 0.35s cubic-bezier(.4,0,.2,1), opacity 0.25s;
}
.slide-right-modal-enter-from,
.slide-right-modal-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
.slide-right-modal-enter-to,
.slide-right-modal-leave-from {
  transform: translateX(0);
  opacity: 1;
}
.profile-modal__close {
  position: absolute;
  top: 12px;
  right: 16px;
  font-size: 2.5rem;
  cursor: pointer;
  background: none;
  border: none;
}
</style>
